import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDepartments } from '@/hooks/use-organizations';
import { useCreateVacancy, usePipelineTemplates } from '@/hooks/use-recruitment';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { EmployeeMultiSelect, type EmployeeOption } from './employee-multi-select';

interface CreateVacancyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (id: string) => void;
}

export function CreateVacancyDialog({ open, onOpenChange, onSuccess }: CreateVacancyDialogProps) {
  const [title, setTitle] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [pipelineTemplateId, setPipelineTemplateId] = useState('');
  const [recruiters, setRecruiters] = useState<EmployeeOption[]>([]);
  const [hiringTeam, setHiringTeam] = useState<EmployeeOption[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: departments = [] } = useDepartments();
  const { data: pipelineTemplates = [], isLoading: templatesLoading } = usePipelineTemplates();
  const createMutation = useCreateVacancy();

  // Reset form on close
  useEffect(() => {
    if (!open) {
      setTitle('');
      setDepartmentId('');
      setPipelineTemplateId('');
      setRecruiters([]);
      setHiringTeam([]);
      setErrors({});
    }
  }, [open]);

  // Auto-select default pipeline template
  useEffect(() => {
    if (open && pipelineTemplates.length > 0 && !pipelineTemplateId) {
      const defaultTemplate = pipelineTemplates.find((t) => t.isDefault) ?? pipelineTemplates[0];
      setPipelineTemplateId(defaultTemplate.id);
    }
  }, [open, pipelineTemplates, pipelineTemplateId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};
    if (!title.trim()) newErrors.title = 'Title is required';
    if (!departmentId) newErrors.departmentId = 'Department is required';
    if (recruiters.length === 0) newErrors.recruiters = 'At least one recruiter is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    try {
      const vacancy = await createMutation.mutateAsync({
        title: title.trim(),
        departmentId,
        pipelineTemplateId: pipelineTemplateId || undefined,
        recruiterIds: recruiters.map((r) => r.id),
        hiringTeamIds: hiringTeam.length ? hiringTeam.map((h) => h.id) : undefined,
      });
      onOpenChange(false);
      onSuccess(vacancy.id);
    } catch (err) {
      const msg = err instanceof AxiosError ? err.response?.data?.message : undefined;
      setErrors({
        submit: msg ?? (err instanceof Error ? err.message : 'Failed to create vacancy'),
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New Vacancy</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Senior Frontend Developer"
              maxLength={200}
              autoFocus
              aria-invalid={!!errors.title}
            />
            {errors.title && <p className="text-sm text-destructive mt-1">{errors.title}</p>}
          </div>

          <div>
            <Label>Department *</Label>
            <Select value={departmentId} onValueChange={setDepartmentId}>
              <SelectTrigger>
                <SelectValue placeholder="Select department..." />
              </SelectTrigger>
              <SelectContent>
                {departments.map((d: { id: string; name: string }) => (
                  <SelectItem
                    key={d.id}
                    value={d.id}
                    className="[&>span:last-child]:truncate"
                    title={d.name}
                  >
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.departmentId && (
              <p className="text-sm text-destructive mt-1">{errors.departmentId}</p>
            )}
          </div>

          <div>
            <Label>Hiring Pipeline Template</Label>
            {!templatesLoading && pipelineTemplates.length === 0 ? (
              // Radix Select renders a zero-height popover when no <SelectItem> children
              // exist, which surfaces as a blank red-bordered strip overlapping the next
              // field (HW-420 / HW-441). Show a static fallback in that case instead.
              <div className="flex h-9 items-center rounded-md border border-dashed border-border px-3 text-sm text-muted-foreground">
                No templates available yet
              </div>
            ) : (
              <Select value={pipelineTemplateId} onValueChange={setPipelineTemplateId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select template..." />
                </SelectTrigger>
                <SelectContent>
                  {pipelineTemplates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} ({t.stages.length} stages)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div>
            <Label>Responsible Recruiter *</Label>
            <EmployeeMultiSelect
              value={recruiters}
              onChange={setRecruiters}
              excludeIds={hiringTeam.map((h) => h.id)}
              ariaInvalid={!!errors.recruiters}
            />
            {errors.recruiters && (
              <p className="text-sm text-destructive mt-1">{errors.recruiters}</p>
            )}
          </div>

          <div>
            <Label>Hiring Team (Managers & Interviewers)</Label>
            <p className="text-xs text-muted-foreground mt-0.5 mb-2">
              Read-only access to vacancy details. Can view and comment on candidates.
            </p>
            <EmployeeMultiSelect
              value={hiringTeam}
              onChange={setHiringTeam}
              excludeIds={recruiters.map((r) => r.id)}
            />
          </div>

          {errors.submit && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {errors.submit}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" variant="accent" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
